import { IUndoRedoService, type UndoSource } from '../../undo/common/undo.js';
import { ILogService } from '../../log/common/log.js';
import { registerSingleton } from '../../instantiation/common/registry.js';
import { SyncDescriptor } from '../../instantiation/common/descriptors.js';
import { IDisposable } from '../../../base/common/lifecycle.js';
import { Emitter, Event } from '../../../base/common/events.js';
import { IInstantiationService } from '../../instantiation/common/instantiation.js';
import { AICommandResult, CommandServiceDidChangeEvent, CommandsRegistry, ExecuteCommandResult, ICommand, ICommandCanExecuteReturnValue, ICommandContextUser, ICommandContextAI, ICommandEvent, ICommandService, isUndoableCommand } from './commands.js';
import { IAnalyticsService } from '../../analytics/common/analytics.js';
import { ITracingService } from '../../tracing/common/tracing.js';



// export const CommandService

/**
 * Default implementation of ICommandService
 */
export class CommandService implements ICommandService {
	static get(): ICommandService {
		if (!this._instance) {
			throw new Error("CommandService instance not initialized yet");
		}
		return this._instance;
	}
	private static _instance: CommandService | null = null;



	constructor(
		@IInstantiationService private readonly instantiationService: IInstantiationService,
		@IUndoRedoService private readonly undoService: IUndoRedoService,
		@ILogService private readonly logger: ILogService,
		@ITracingService private readonly tracing?: ITracingService,
	) {
		CommandService._instance = this;
	}

	private readonly _onDidChange = new Emitter<CommandServiceDidChangeEvent>();
	readonly onDidChange = this._onDidChange.event;

	private readonly _onWillExecuteCommand = new Emitter<ICommandEvent>();
	readonly onWillExecuteCommand = this._onWillExecuteCommand.event;
	private readonly _onDidExecuteCommand = new Emitter<ICommandEvent>();
	readonly onDidExecuteCommand = this._onDidExecuteCommand.event;
	private localUserSource?: UndoSource;
	private anonymousAiSource?: UndoSource;
	private readonly aiConversationSources = new Map<string, UndoSource>();

	private sourceFor(context?: ICommandContextUser | ICommandContextAI): UndoSource {
		if (context?.undoSource) return context.undoSource;
		if (context?.isAi) {
			const slug = context.conversation?.slug;
			if (!slug) return this.anonymousAiSource ??= this.undoService.createSource({ origin: "ai" });
			let source = this.aiConversationSources.get(slug);
			if (!source) {
				source = this.undoService.createSource({ origin: "ai", sessionId: slug });
				this.aiConversationSources.set(slug, source);
			}
			return source;
		}
		return this.localUserSource ??= this.undoService.createSource({ origin: "local-user" });
	}


	// Called to execute a typed command with command class inference
	async executeCommand<TCommand extends ICommand<any, any> = ICommand<unknown, unknown>>(
		id: string,
		args: TCommand extends ICommand<infer TArgs, any> ? TArgs : unknown,
		context?: ICommandContextUser
	): Promise<ExecuteCommandResult<TCommand extends ICommand<any, infer TResult> ? TResult : unknown>>;

	// Called to execute a command for AI context
	async executeCommand(id: string, args: unknown, context: ICommandContextAI): Promise<ExecuteCommandResult<AICommandResult>>;

	async executeCommand<TResult = unknown>(id: string, args: unknown, context?: ICommandContextUser | ICommandContextAI): Promise<ExecuteCommandResult<TResult> | ExecuteCommandResult<AICommandResult>> {
		const command = CommandsRegistry.getCommand(id);
		if (!command) {
			this.logger.error(`Command not found: ${id}`);
			throw new Error(`Command '${id}' not found`);
		}

		// Validate if possible
		if (command.canExecute) {
			const res = command.canExecute(args);
			if (res.canExecute === false) {
				this.logger.warn(`Command '${id}' validation failed (reason: ${res.reason || 'unknown'})`);
				return {
					success: false,
					message: `Cannot execute command '${id}': ${res.reason || 'Unknown reason'}`,
					result: null as TResult
				};
			}
		}

		this.logger.debug(`Executing command: ${id}`);

		// UNDOABLE commands run SERIALIZED per id: the registry holds ONE command
		// instance, and perform → toAction reads/clears per-invocation state on it
		// (oldValue, emittedOpIds). Two in-flight ASYNC invocations interleave that
		// state — the HDRI drop's concurrent background+environment setProperty made
		// each undo clone capture the other's (or a cleared) previous value, and
		// undo restored `undefined` into the envmap. Sync commands never overlap on
		// one thread, so the common path only pays a settled-promise hop.
		if (isUndoableCommand(command) && command.pushToUndoStack !== false) {
			const run = () => this.runCommand<TResult>(id, command, args, context);
			const previous = this.undoableTails.get(id);
			const chained = previous ? previous.then(run, run) : run();
			const tail = chained.then(() => void 0, () => void 0);
			this.undoableTails.set(id, tail);
			void tail.finally(() => {
				if (this.undoableTails.get(id) === tail) this.undoableTails.delete(id);
			});
			return chained;
		}
		return this.runCommand<TResult>(id, command, args, context);
	}

	/** in-flight undoable executions per command id — see executeCommand */
	private readonly undoableTails = new Map<string, Promise<void>>();

	private async runCommand<TResult>(id: string, command: ICommand, args: unknown, context?: ICommandContextUser | ICommandContextAI): Promise<ExecuteCommandResult<TResult>> {
		const span = this.tracing?.startSpan(`command:${id}`);
		let outcome: 'success' | 'failed' = 'failed';
		try {
			// Execute the command
			return await this.instantiationService.invokeFunction(async (accessor) => {
				this._onWillExecuteCommand.fire({ commandId: id, args: [args] });

				// Use executeForAI if AI context is requested and available, otherwise fallback to execute
				const res = await
					(context?.isAi && command.executeForAI
						? command.executeForAI(accessor, args, context)
						: command.execute(accessor, args, context)
					);

				this._onDidExecuteCommand.fire({ commandId: id, args: [args] });

				let result: TResult = res as TResult;

				// If invoked for AI, ensure result is a string
				if (context?.isAi) {
					if (typeof res === "string") {
						result = res as TResult;
					}
					else if (typeof res === "number" || typeof res === "boolean") {
						result = String(res) as TResult;
					} else if (res === null || res === undefined) {
						result = `Command '${id}' completed successfully` as TResult;
					} else {
						// For complex objects, use a generic success message
						result = `Command '${id}' completed successfully` as TResult;
					}
				}

				// Auto-push to undo stack if undoable. View-state commands (selection,
				// expand, navigation, camera toggles) opt out via pushToUndoStack=false —
				// they'd otherwise bury real edits under invisible entries
				// (plans/undo-selection-stack-issue.md).
				if (isUndoableCommand(command) && command.pushToUndoStack !== false) {
					const action = command.toAction(this.instantiationService, args, context);
					this.undoService.push(action, { group: context?.undoGroup, source: this.sourceFor(context) });
				}

				outcome = 'success';
				return {
					success: true,
					message: `Command '${id}' executed successfully`,
					result
				};
			});

		} catch (error) {
			this.logger.error(`Error executing command '${id}':`, error);
			throw error;
		} finally {
			span?.end(outcome);
		}
	}

	canExecuteCommand(id: string, args: unknown): ICommandCanExecuteReturnValue {
		const command = CommandsRegistry.getCommand(id);
		if (!command) {
			return { canExecute: false, reason: `Command '${id}' not found` };
		}

		if (command.canExecute) {
			return command.canExecute(args);
		}

		return {
			canExecute: true
		}
	}
}

registerSingleton(ICommandService, new SyncDescriptor(CommandService));


